// ============================================
// pages/Assinatura.jsx - Scriptus Pharma Partner
// Gerenciamento de Assinatura para Farmácias
// 
// ✅ CORRIGIDO: Integração completa com backend
// ✅ CORRIGIDO: Estrutura padronizada PT-BR
// ✅ CORRIGIDO: Usa helpers do AuthContext
// ✅ CORRIGIDO: Mapeamento de campos alinhado
// ✅ ATUALIZADO: Removido botão cancelar, gerenciar ocupa largura total
// 
// ESTRUTURA PADRONIZADA:
// - plano: "basic" | "partner"
// - statusAssinatura: "ativa" | "pendente" | "cancelada" | "inativa"
// - stripe: { customerId, subscriptionId, account }
// - assinatura: { iniciadaEm, proximaCobranca, canceladaEm, acessoAte, cancelaNoFimDoPeriodo }
// ============================================

import { useState, useEffect, useCallback } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import { fetchWithAuth, handleApiError } from "../utils/authHelpers";
import HeaderMenu from "../components/HeaderMenu";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { 
  CheckCircle, 
  Zap, 
  Settings, 
  Sparkles, 
  Shield, 
  TrendingUp, 
  Users, 
  Loader2,
  AlertTriangle,
  RefreshCw,
  Star,
  MapPin,
  MessageSquare,
  BarChart3,
  Crown,
  FileText,
  Package,
  Infinity,
  X,
  Calendar,
  CreditCard,
  AlertCircle,
  ExternalLink
} from "lucide-react";

// 🔧 Configuração da API
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.scriptusrxia.com.br';

// ============================================
// Componente: GlassCard
// ============================================
function GlassCard({ children, className = '', elevated = false }) {
  return (
    <div className={`${elevated ? 'glass-card-elevated-refined' : 'glass-card-refined'} rounded-2xl ${className}`}>
      {children}
    </div>
  );
}

// ============================================
// Componente Principal: Assinatura
// ============================================
export default function Assinatura() {
  // ✅ USAR HELPERS DO AUTHCONTEXT
  const { 
    user: authUser, 
    pharmacy,
    refreshPharmacyData,
    // ✅ Helpers padronizados do AuthContext
    isPartner,
    isCanceladaMasAtiva,
    tipoPlano,
    statusAssinatura,
    helpers
  } = useAuth();
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [farmacia, setFarmacia] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [showCancelModal, setShowCancelModal] = useState(false);

  // ============================================
  // Verificar parâmetros de URL (success/canceled)
  // ============================================
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    const plan = searchParams.get('plan');
    const source = searchParams.get('source');

    if (success === 'true') {
      // ✅ Pagamento realizado com sucesso
      if (plan === 'partner' || source === 'stripe_checkout') {
        toast.success('🎉 Assinatura Partner ativada com sucesso!', { duration: 5000 });
      } else {
        toast.success('🎉 Operação realizada com sucesso!');
      }
      
      // Limpar parâmetros da URL
      navigate('/assinatura', { replace: true });
      
      // Recarregar dados da farmácia
      setTimeout(() => {
        buscarDadosFarmacia();
      }, 1000);
      
    } else if (canceled === 'true') {
      toast.error('Processo de pagamento cancelado.');
      navigate('/assinatura', { replace: true });
    }
  }, [searchParams, navigate]);

  // Efeito para detectar scroll
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ============================================
  // ✅ Buscar dados da farmácia (ESTRUTURA PADRONIZADA)
  // ============================================
  const buscarDadosFarmacia = useCallback(async () => {
    if (!authUser?.uid) {
      setCarregando(false);
      return;
    }
    
    try {
      const docRef = doc(db, "pharmacies", authUser.uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setFarmacia(data);
        
        // ✅ Extrair detalhes da assinatura (ESTRUTURA PADRONIZADA PT-BR)
        const assinatura = data.assinatura || {};
        const stripeData = data.stripe || {};
        
        setSubscriptionDetails({
          // ✅ Campos novos (PT-BR) com fallback para legados
          status: data.statusAssinatura || assinatura.status,
          proximaCobranca: assinatura.proximaCobranca || assinatura.current_period_end || assinatura.currentPeriodEnd,
          cancelaNoFimDoPeriodo: assinatura.cancelaNoFimDoPeriodo || assinatura.cancel_at_period_end || assinatura.cancelAtPeriodEnd,
          acessoAte: assinatura.acessoAte,
          canceladaEm: assinatura.canceladaEm,
          iniciadaEm: assinatura.iniciadaEm,
          
          // ✅ Dados Stripe (novo local)
          customerId: stripeData.customerId || assinatura.customerId,
          subscriptionId: stripeData.subscriptionId || assinatura.subscriptionId,
          account: stripeData.account || assinatura.account || 'scriptus'
        });
      }
    } catch (error) {
      console.error("Erro ao buscar farmácia:", error);
      handleApiError(error, navigate);
    }
    setCarregando(false);
  }, [authUser, navigate]);

  useEffect(() => {
    buscarDadosFarmacia();
  }, [buscarDadosFarmacia]);

  // Mostrar mensagem
  const showMessage = useCallback((msg, type = 'info') => {
    setMensagem(msg);
    setMessageType(type);
    setTimeout(() => setMensagem(''), 5000);
  }, []);

  // ============================================
  // ✅ VALORES DERIVADOS (usando AuthContext quando possível)
  // ============================================
  
  // Usar helpers do AuthContext para pharmacy global
  // Mas também suportar farmacia local (recarregado)
  const planoAtual = useCallback(() => {
    // Priorizar dados locais (mais atualizados após refresh)
    if (farmacia) {
      return helpers.getTipoPlano(farmacia);
    }
    // Fallback para pharmacy do context
    return tipoPlano;
  }, [farmacia, tipoPlano, helpers]);

  const isPartnerAtivo = useCallback(() => {
    // Priorizar dados locais
    if (farmacia) {
      return helpers.isPartnerAtivo(farmacia);
    }
    // Fallback para isPartner do context
    return isPartner;
  }, [farmacia, isPartner, helpers]);

  const isCanceladoMasAtivo = useCallback(() => {
    // Priorizar dados locais
    if (farmacia) {
      return helpers.isAssinaturaCanceladaMasAtiva(farmacia);
    }
    // Fallback para context
    return isCanceladaMasAtiva;
  }, [farmacia, isCanceladaMasAtiva, helpers]);

  // Valores computados para uso no render
  const plano = planoAtual();
  const partnerAtivo = isPartnerAtivo();
  const canceladoMasAtivo = isCanceladoMasAtivo();

  // ============================================
  // ✅ Handler: Criar Checkout Session para Partner (SIMPLIFICADO)
  // ============================================
  const handleAtivarAssinatura = useCallback(async () => {
    setLoadingAction(true);
    
    try {
      if (!authUser) {
        showMessage("Você precisa estar autenticado para realizar esta ação.", "warning");
        navigate("/login");
        return;
      }

      toast.loading('Preparando checkout...', { id: 'checkout' });

      // ✅ SIMPLIFICADO: uid e email já vêm do token via requireAuthPharmacy
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/payment/pharmacy/create-checkout-session`,
        {
          method: "POST",
          body: JSON.stringify({
            source: "assinatura" // ✅ Indica origem para URL de retorno
          }),
        }
      );
      
      const data = await response.json();
      
      toast.dismiss('checkout');

      if (!response.ok || data.error) {
        const errorMsg = data.error?.message || data.error || "Erro ao criar sessão de checkout";
        throw new Error(errorMsg);
      }

      // ✅ Se já tem assinatura ativa
      if (data.hasActiveSubscription) {
        toast.success('Você já possui uma assinatura Partner ativa!');
        await buscarDadosFarmacia();
        return;
      }

      // ✅ Se foi reativada
      if (data.reativado) {
        toast.success('Assinatura reativada com sucesso!');
        if (refreshPharmacyData) await refreshPharmacyData();
        await buscarDadosFarmacia();
        return;
      }
      
      // ✅ Redirecionar para checkout Stripe
      const checkoutUrl = data.url || data.sessionUrl;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        throw new Error("URL de checkout não recebida");
      }
    } catch (error) {
      toast.dismiss('checkout');
      console.error("Erro ao ativar assinatura:", error);
      
      const handled = handleApiError(error, navigate);
      if (!handled) {
        showMessage(error.message || "Erro ao processar assinatura. Tente novamente.", "error");
        toast.error(error.message || "Erro ao processar assinatura");
      }
    } finally {
      setLoadingAction(false);
    }
  }, [authUser, navigate, showMessage, refreshPharmacyData, buscarDadosFarmacia]);

  // ============================================
  // Handler: Abrir Portal de Billing Stripe
  // ============================================
  const handleGerenciarAssinatura = useCallback(async () => {
    setLoadingAction(true);
    
    try {
      if (!authUser) {
        showMessage("Você precisa estar autenticado.", "warning");
        navigate("/login");
        return;
      }

      toast.loading('Abrindo portal...', { id: 'portal' });

      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/payment/pharmacy/create-portal-session`,
        {
          method: "POST",
          body: JSON.stringify({}),
        }
      );
      
      const data = await response.json();
      
      toast.dismiss('portal');

      if (!response.ok || data.error) {
        const errorMsg = data.error?.message || data.error || "Erro ao abrir portal";
        throw new Error(errorMsg);
      }
      
      const portalUrl = data.url || data.sessionUrl;
      if (portalUrl) {
        window.location.href = portalUrl;
      } else {
        throw new Error("URL do portal não recebida");
      }
    } catch (error) {
      toast.dismiss('portal');
      console.error("Erro ao abrir portal:", error);
      showMessage(error.message || "Erro ao abrir portal de assinatura.", "error");
      toast.error(error.message || "Erro ao abrir portal");
    } finally {
      setLoadingAction(false);
    }
  }, [authUser, navigate, showMessage]);

  // ============================================
  // ✅ Handler: Cancelar Assinatura (CAMPO CORRIGIDO)
  // ============================================
  const handleCancelarAssinatura = useCallback(async () => {
    setLoadingAction(true);
    setShowCancelModal(false);
    
    try {
      toast.loading('Processando cancelamento...', { id: 'cancel' });

      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/payment/pharmacy/cancel`,
        {
          method: "POST",
          body: JSON.stringify({
            motivo: "Solicitação do usuário via painel" // ✅ CORRIGIDO: era 'reason'
          }),
        }
      );
      
      const data = await response.json();
      
      toast.dismiss('cancel');

      if (!response.ok || data.error) {
        const errorMsg = data.error?.message || data.error || "Erro ao cancelar assinatura";
        throw new Error(errorMsg);
      }
      
      // ✅ Backend retorna 'acessoAteFormatado' ou 'acessoAte'
      const accessUntil = data.acessoAteFormatado || data.accessUntilFormatted || data.acessoAte || data.accessUntil;
      toast.success(`Assinatura cancelada. Acesso até ${accessUntil}`);
      showMessage(`Sua assinatura foi cancelada. Você terá acesso até ${accessUntil}.`, "info");
      
      // Atualizar dados
      if (refreshPharmacyData) await refreshPharmacyData();
      await buscarDadosFarmacia();
      
    } catch (error) {
      toast.dismiss('cancel');
      console.error("Erro ao cancelar:", error);
      showMessage(error.message || "Erro ao cancelar assinatura.", "error");
      toast.error(error.message || "Erro ao cancelar assinatura");
    } finally {
      setLoadingAction(false);
    }
  }, [refreshPharmacyData, showMessage, buscarDadosFarmacia]);

  // ============================================
  // Handler: Reativar Assinatura
  // ============================================
  const handleReativarAssinatura = useCallback(async () => {
    setLoadingAction(true);
    
    try {
      toast.loading('Reativando assinatura...', { id: 'reactivate' });

      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/v1/payment/pharmacy/reactivate`,
        {
          method: "POST",
          body: JSON.stringify({}),
        }
      );
      
      const data = await response.json();
      
      toast.dismiss('reactivate');

      if (!response.ok || data.error) {
        // ✅ Se assinatura já expirou, redirecionar para novo checkout
        if (data.error?.code === 'assinatura/expirada') {
          toast.error('Assinatura expirada. Criando nova assinatura...');
          await handleAtivarAssinatura();
          return;
        }
        throw new Error(data.error?.message || data.error || "Erro ao reativar");
      }
      
      toast.success('Assinatura reativada com sucesso!');
      
      if (refreshPharmacyData) await refreshPharmacyData();
      await buscarDadosFarmacia();
      
    } catch (error) {
      toast.dismiss('reactivate');
      console.error("Erro ao reativar:", error);
      showMessage(error.message || "Erro ao reativar assinatura.", "error");
      toast.error(error.message || "Erro ao reativar assinatura");
    } finally {
      setLoadingAction(false);
    }
  }, [refreshPharmacyData, showMessage, buscarDadosFarmacia, handleAtivarAssinatura]);

  // ============================================
  // ✅ Helper: Formatar data (suporta múltiplos formatos)
  // ============================================
  const formatEndDate = (timestamp) => {
    if (!timestamp) return null;
    
    let date;
    if (typeof timestamp === 'number') {
      // Unix timestamp (segundos)
      date = new Date(timestamp * 1000);
    } else if (typeof timestamp === 'string') {
      // ISO string
      date = new Date(timestamp);
    } else if (timestamp?.toDate) {
      // Firestore Timestamp
      date = timestamp.toDate();
    } else {
      return null;
    }
    
    if (isNaN(date.getTime())) return null;
    
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // ============================================
  // ✅ Helper: Obter próxima cobrança (estrutura padronizada)
  // ============================================
  const getProximaCobranca = () => {
    // Priorizar estrutura nova
    const proximaCobranca = 
      subscriptionDetails?.proximaCobranca ||
      farmacia?.assinatura?.proximaCobranca ||
      // Fallback para estrutura legada
      subscriptionDetails?.currentPeriodEnd ||
      farmacia?.assinatura?.current_period_end ||
      farmacia?.assinatura?.currentPeriodEnd;
    
    return formatEndDate(proximaCobranca);
  };

  // ============================================
  // ✅ Helper: Obter data de acesso até (cancelamento)
  // ============================================
  const getAcessoAte = () => {
    const acessoAte = 
      subscriptionDetails?.acessoAte ||
      farmacia?.assinatura?.acessoAte ||
      // Fallback
      subscriptionDetails?.proximaCobranca ||
      farmacia?.assinatura?.proximaCobranca ||
      farmacia?.assinatura?.current_period_end;
    
    return formatEndDate(acessoAte);
  };

  // ============================================
  // Estado: Carregando
  // ============================================
  if (carregando) {
    return (
      <div className="min-h-screen bg-gradient-refined flex items-center justify-center px-4">
        <div className="fixed inset-0 z-0 pointer-events-none">
          <div className="background-pattern-refined"></div>
          <div className="background-glow-1"></div>
          <div className="background-glow-2"></div>
        </div>
        <GlassCard elevated className="p-8 shadow-xl max-w-sm w-full relative z-10">
          <div className="loading-container-refined">
            <div className="loading-animation-refined">
              <div className="loading-glow-refined"></div>
              <div className="loading-icon-refined">
                <Loader2 className="w-7 h-7 text-white animate-spin" />
              </div>
            </div>
            <div className="text-center">
              <h3 className="sf-pro-display text-slate-900 font-bold text-base">Carregando...</h3>
              <p className="sf-pro-text text-slate-500 text-xs mt-1.5">Buscando dados da assinatura</p>
            </div>
          </div>
        </GlassCard>
        <style>{styles}</style>
      </div>
    );
  }

  // ============================================
  // Estado: Erro ao carregar
  // ============================================
  if (!farmacia) {
    return (
      <div className="min-h-screen bg-gradient-refined flex items-center justify-center p-4">
        <div className="fixed inset-0 z-0 pointer-events-none">
          <div className="background-pattern-refined"></div>
          <div className="background-glow-1"></div>
          <div className="background-glow-2"></div>
        </div>
        <GlassCard elevated className="p-8 shadow-xl max-w-md w-full text-center relative z-10">
          <div className="status-icon-refined bg-gradient-to-br from-red-500 to-red-600 mx-auto mb-4">
            <AlertTriangle className="w-6 h-6 text-white" />
          </div>
          <h3 className="sf-pro-display font-bold text-slate-900 text-lg mb-2">Erro ao carregar</h3>
          <p className="sf-pro-text text-slate-600 text-sm mb-6">Não foi possível carregar os dados da farmácia.</p>
          <button 
            onClick={() => window.location.reload()}
            className="button-secondary-refined w-full"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="sf-pro-text">Tentar Novamente</span>
          </button>
        </GlassCard>
        <style>{styles}</style>
      </div>
    );
  }

  // ============================================
  // Recursos dos planos
  // ============================================
  const recursosBasic = [
    { icon: FileText, text: "50 dispensações/mês" },
    { icon: Shield, text: "Validação ICP-Brasil" },
    { icon: FileText, text: "XML SNGPC automático" },
    { icon: Package, text: "Histórico básico" }
  ];

  const recursosPartner = [
    { icon: Infinity, text: "Dispensações ilimitadas", highlight: true },
    { icon: MapPin, text: "Visibilidade Marketplace", highlight: true },
    { icon: MessageSquare, text: "Leads via WhatsApp", highlight: true },
    { icon: BarChart3, text: "Analytics avançados", highlight: false },
    { icon: Users, text: "Suporte prioritário", highlight: false },
    { icon: Shield, text: "XML SNGPC completo", highlight: false }
  ];

  // ============================================
  // Render Principal
  // ============================================
  return (
    <div className="min-h-screen bg-gradient-refined relative overflow-x-hidden">
      
      {/* Background Pattern */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="background-pattern-refined"></div>
        <div className="background-glow-1"></div>
        <div className="background-glow-2"></div>
        <div className="background-glow-3"></div>
      </div>

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? 'header-scrolled-refined' : 'header-refined'
      }`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="logo-link-refined">
              <img src="/logo.PNG" alt="Scriptus RxIA" className="h-5 w-auto" />
            </Link>
            <HeaderMenu />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 sm:pt-24 pb-12 relative z-10">
        
        {/* Page Title */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-xl sm:text-2xl sf-pro-display font-medium text-gray-900 tracking-tight mb-1.5">
            Gerenciar Assinatura
          </h1>
          <p className="text-xs sm:text-sm sf-pro-text text-gray-500 font-normal">
            Desbloqueie todo o potencial do Scriptus Pharma Partner
          </p>
        </div>

        {/* ============================================ */}
        {/* Card de Status Partner (quando ativo) */}
        {/* ============================================ */}
        {partnerAtivo && (
          <GlassCard elevated className="p-5 sm:p-6 mb-6 shadow-xl">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="main-icon-refined bg-gradient-to-br from-emerald-100 to-emerald-200/80">
                  <Crown className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="card-title-refined">Plano Partner</h3>
                    <span className="partner-badge-active">
                      <CheckCircle className="w-3 h-3" />
                      Ativo
                    </span>
                  </div>
                  <p className="sf-pro-text text-slate-500 text-xs mt-0.5">
                    Dispensações ilimitadas • Marketplace • Leads
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="sf-pro-display font-bold text-lg text-emerald-600">R$ 100</p>
                  <p className="sf-pro-text text-slate-500 text-xs">/mês</p>
                </div>
              </div>
            </div>

            {/* Informações da Assinatura */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5 pt-5 border-t border-slate-200/50">
              <div className="subscription-info-item">
                <Calendar className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="sf-pro-text text-slate-500 text-xs">Próxima cobrança</p>
                  <p className="sf-pro-text text-slate-900 text-sm font-medium">
                    {getProximaCobranca() || 'N/A'}
                  </p>
                </div>
              </div>

              <div className="subscription-info-item">
                <CreditCard className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="sf-pro-text text-slate-500 text-xs">Status</p>
                  <p className={`sf-pro-text text-sm font-medium ${
                    canceladoMasAtivo ? 'text-amber-600' : 'text-emerald-600'
                  }`}>
                    {canceladoMasAtivo ? 'Cancelado (ativo até fim do período)' : 'Ativo'}
                  </p>
                </div>
              </div>
            </div>

            {/* Aviso de Cancelamento */}
            {canceladoMasAtivo && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="sf-pro-text text-amber-800 text-sm font-medium">
                      Assinatura cancelada
                    </p>
                    <p className="sf-pro-text text-amber-700 text-xs mt-1">
                      Você ainda tem acesso ao plano Partner até{' '}
                      <strong>{getAcessoAte() || 'o fim do período'}</strong>.
                      Após essa data, sua conta voltará para o plano Basic.
                    </p>
                    <button
                      onClick={handleReativarAssinatura}
                      disabled={loadingAction}
                      className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg text-xs font-medium transition-all"
                    >
                      {loadingAction ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3.5 h-3.5" />
                      )}
                      Reativar Assinatura
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Botão de Gerenciar Pagamento - Largura Total */}
            <div className="mt-5">
              <button
                onClick={handleGerenciarAssinatura}
                disabled={loadingAction}
                className="button-secondary-refined w-full"
              >
                {loadingAction ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Settings className="w-4 h-4" />
                )}
                <span>Gerenciar Pagamento</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>
          </GlassCard>
        )}

        {/* ============================================ */}
        {/* Card Principal - Planos */}
        {/* ============================================ */}
        <GlassCard elevated className="p-5 sm:p-6 mb-6 shadow-2xl">
          
          {/* Header do Card */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
            <div className="flex items-center space-x-2.5">
              <div className="main-icon-refined">
                <Crown className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="card-title-refined">
                  {partnerAtivo ? 'Seu Plano Atual' : 'Escolha seu Plano'}
                </h2>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="badge-icp-refined">
                    <Shield className="w-2.5 h-2.5" />
                    ICP-Brasil
                  </span>
                  <span className="badge-anvisa-refined">
                    <FileText className="w-2.5 h-2.5" />
                    ANVISA
                  </span>
                </div>
              </div>
            </div>

            {/* Status Badge */}
            <div className={`status-badge-refined ${partnerAtivo ? 'status-badge-success' : 'status-badge-info'}`}>
              {partnerAtivo ? (
                <>
                  <CheckCircle className="w-3.5 h-3.5" />
                  <span>Partner Ativo</span>
                </>
              ) : (
                <>
                  <Shield className="w-3.5 h-3.5" />
                  <span>Plano Basic</span>
                </>
              )}
            </div>
          </div>

          {/* Grid de Planos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            
            {/* Card Basic */}
            <div className={`plan-card-refined ${!partnerAtivo ? 'plan-card-active' : ''}`}>
              <div className="plan-card-header">
                <div className="flex items-center gap-2.5">
                  <div className="plan-icon-refined plan-icon-basic">
                    <Shield className="w-4 h-4 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="sf-pro-display font-bold text-slate-900 text-sm">Basic</h3>
                    <p className="sf-pro-text text-slate-500 text-xs">Essencial para começar</p>
                  </div>
                </div>
                {!partnerAtivo && (
                  <span className="plan-badge-current">
                    <CheckCircle className="w-3 h-3" />
                    Atual
                  </span>
                )}
              </div>

              <div className="plan-price-refined">
                <span className="price-value">Grátis</span>
              </div>

              <div className="plan-features-refined">
                {recursosBasic.map((item, i) => (
                  <div key={i} className="plan-feature-item">
                    <div className="plan-feature-icon plan-feature-icon-basic">
                      <item.icon className="w-3 h-3" />
                    </div>
                    <span className="sf-pro-text text-slate-600 text-xs">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Card Partner */}
            <div className={`plan-card-refined plan-card-partner ${partnerAtivo ? 'plan-card-active plan-card-partner-active' : ''}`}>
              <div className="plan-card-header">
                <div className="flex items-center gap-2.5">
                  <div className="plan-icon-refined plan-icon-partner">
                    <Crown className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="sf-pro-display font-bold text-slate-900 text-sm">Partner</h3>
                    <p className="sf-pro-text text-emerald-600 text-xs font-medium">Marketplace + Leads</p>
                  </div>
                </div>
                {partnerAtivo ? (
                  <span className="plan-badge-active">
                    <CheckCircle className="w-3 h-3" />
                    Ativo
                  </span>
                ) : (
                  <span className="plan-badge-recommended">
                    <Star className="w-3 h-3 fill-current" />
                    Recomendado
                  </span>
                )}
              </div>

              <div className="plan-price-refined">
                <span className="price-value">R$ 100</span>
                <span className="price-period">/mês</span>
              </div>

              <div className="plan-features-refined">
                {recursosPartner.map((item, i) => (
                  <div key={i} className={`plan-feature-item ${item.highlight ? 'plan-feature-highlight' : ''}`}>
                    <div className={`plan-feature-icon ${item.highlight ? 'plan-feature-icon-partner' : 'plan-feature-icon-basic'}`}>
                      <item.icon className="w-3 h-3" />
                    </div>
                    <span className={`sf-pro-text text-xs ${item.highlight ? 'text-slate-900 font-medium' : 'text-slate-600'}`}>
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Botão Dinâmico */}
          {!partnerAtivo && (
            <button
              onClick={handleAtivarAssinatura}
              disabled={loadingAction}
              className="button-primary-refined w-full"
            >
              {loadingAction ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Processando...</span>
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  <span>Fazer Upgrade para Partner - R$ 100/mês</span>
                </>
              )}
            </button>
          )}

          {!partnerAtivo && (
            <p className="sf-pro-text text-xs text-slate-400 text-center mt-3">
              Pagamento seguro via Stripe • Cancele quando quiser • Sem fidelidade
            </p>
          )}
        </GlassCard>

        {/* ============================================ */}
        {/* Como Funciona o Marketplace */}
        {/* ============================================ */}
        <GlassCard elevated className="p-5 sm:p-6 shadow-2xl">
          <div className="flex items-center gap-3 mb-5">
            <div className="main-icon-refined bg-gradient-to-br from-blue-100 to-blue-200/80">
              <Sparkles className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="card-title-refined">Como funciona o Marketplace</h3>
              <p className="sf-pro-text text-slate-500 text-xs mt-0.5">
                Conecte-se com médicos da sua região
              </p>
            </div>
          </div>

          {/* Steps Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            {[
              { step: "1", title: "Perfil destacado", desc: "Apareça para médicos próximos" },
              { step: "2", title: "Médico recomenda", desc: "Sua farmácia é sugerida" },
              { step: "3", title: "Lead no WhatsApp", desc: "Receba dados do paciente" },
              { step: "4", title: "Venda garantida", desc: "Paciente pronto para comprar" }
            ].map((item, i) => (
              <div key={i} className="marketplace-step-refined">
                <div className="step-number-refined">
                  {item.step}
                </div>
                <div className="step-content-refined">
                  <p className="sf-pro-text font-semibold text-slate-900 text-xs leading-tight mb-0.5">
                    {item.title}
                  </p>
                  <p className="sf-pro-text text-slate-500 text-[10px] leading-tight">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* ROI Card */}
          <div className="roi-card-refined">
            <div className="roi-icon-refined">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="roi-content-refined">
              <div className="roi-highlight-refined">
                <span className="roi-badge-refined">ROI Garantido</span>
                <p className="sf-pro-display font-bold text-emerald-900 text-sm">
                  2-3 vendas extras/mês já pagam o investimento
                </p>
              </div>
              <p className="sf-pro-text text-emerald-700 text-xs mt-1">
                Cancele quando quiser, sem compromisso • Sem fidelidade • Sem taxas ocultas
              </p>
            </div>
            <div className="roi-decoration"></div>
          </div>
        </GlassCard>
      </main>

      {/* Footer */}
      <footer className="footer-refined">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
            <img src="/logo.PNG" alt="Scriptus" className="h-3.5 w-auto opacity-60" />
            <span className="text-[10px] sf-pro-text text-slate-500">
              Scriptus Tecnologia em Saúde LTDA © 62.420.408/0001-70
            </span>
          </div>
        </div>
      </footer>

      {/* ============================================ */}
      {/* Modal de Cancelamento */}
      {/* ============================================ */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowCancelModal(false)}
          ></div>
          <GlassCard elevated className="relative z-10 max-w-md w-full p-6 shadow-2xl">
            <div className="text-center mb-6">
              <div className="status-icon-refined bg-gradient-to-br from-red-500 to-red-600 mx-auto mb-4">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <h3 className="sf-pro-display font-bold text-slate-900 text-lg mb-2">
                Cancelar assinatura?
              </h3>
              <p className="sf-pro-text text-slate-600 text-sm">
                Você perderá acesso às funcionalidades Partner ao fim do período atual.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <p className="sf-pro-text text-slate-700 text-sm font-medium mb-2">
                O que você perderá:
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-xs sf-pro-text text-slate-600">
                  <X className="w-3.5 h-3.5 text-red-500" />
                  Dispensações ilimitadas
                </li>
                <li className="flex items-center gap-2 text-xs sf-pro-text text-slate-600">
                  <X className="w-3.5 h-3.5 text-red-500" />
                  Visibilidade no Marketplace
                </li>
                <li className="flex items-center gap-2 text-xs sf-pro-text text-slate-600">
                  <X className="w-3.5 h-3.5 text-red-500" />
                  Leads de pacientes
                </li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="button-secondary-refined flex-1"
              >
                <span>Manter Assinatura</span>
              </button>
              <button
                onClick={handleCancelarAssinatura}
                disabled={loadingAction}
                className="button-danger-refined flex-1"
              >
                {loadingAction ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <span>Confirmar Cancelamento</span>
                )}
              </button>
            </div>
          </GlassCard>
        </div>
      )}

      {/* ============================================ */}
      {/* Notificação */}
      {/* ============================================ */}
      {mensagem && (
        <div className="fixed bottom-6 right-6 left-6 sm:left-auto z-50 max-w-sm">
          <div className={`notification-refined ${
            messageType === 'success' ? 'notification-success-refined' 
            : messageType === 'error' ? 'notification-error-refined'
            : messageType === 'warning' ? 'notification-warning-refined'
            : 'notification-info-refined'
          }`}>
            <div className="flex items-center gap-3">
              <div className="notification-icon-refined">
                {messageType === 'success' && <CheckCircle className="w-4 h-4 text-emerald-600" />}
                {messageType === 'error' && <AlertTriangle className="w-4 h-4 text-red-600" />}
                {messageType === 'warning' && <AlertTriangle className="w-4 h-4 text-amber-600" />}
                {messageType === 'info' && <AlertCircle className="w-4 h-4 text-blue-600" />}
              </div>
              <span className="sf-pro-text font-medium text-slate-900 text-xs flex-1">{mensagem}</span>
              <button onClick={() => setMensagem('')} className="notification-close-refined">
                <X className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{styles}</style>
    </div>
  );
}

// ============================================
// Estilos CSS
// ============================================
const styles = `
  /* ===== FONTES ===== */
  .sf-pro-display {
    font-family: -apple-system, "SF Pro Display", BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    -webkit-font-smoothing: antialiased;
  }
  .sf-pro-text {
    font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    -webkit-font-smoothing: antialiased;
  }

  /* ===== BACKGROUND ===== */
  .bg-gradient-refined {
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 25%, #f1f5f9 50%, #ffffff 75%, #ecfdf5 100%);
  }

  .background-pattern-refined {
    position: absolute;
    inset: 0;
    background-image: 
      radial-gradient(circle at 25% 25%, rgba(16, 185, 129, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(20, 184, 166, 0.06) 0%, transparent 50%);
    opacity: 0.7;
  }

  .background-glow-1 {
    position: absolute;
    top: 10%;
    left: 15%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    filter: blur(40px);
  }

  .background-glow-2 {
    position: absolute;
    bottom: 15%;
    right: 20%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(20, 184, 166, 0.06) 0%, transparent 70%);
    border-radius: 50%;
    filter: blur(35px);
  }

  .background-glow-3 {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.02) 0%, transparent 70%);
    border-radius: 50%;
    filter: blur(60px);
  }

  /* ===== GLASS CARDS ===== */
  .glass-card-refined {
    background: rgba(255, 255, 255, 0.80);
    backdrop-filter: blur(24px) saturate(180%);
    -webkit-backdrop-filter: blur(24px) saturate(180%);
    border: 1px solid rgba(255, 255, 255, 0.5);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.9);
    transition: all 0.3s ease;
  }

  .glass-card-elevated-refined {
    background: rgba(255, 255, 255, 0.90);
    backdrop-filter: blur(30px) saturate(200%);
    -webkit-backdrop-filter: blur(30px) saturate(200%);
    border: 1px solid rgba(255, 255, 255, 0.6);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.08), inset 0 2px 0 rgba(255, 255, 255, 0.95);
    transition: all 0.3s ease;
  }

  /* ===== HEADER ===== */
  .header-refined {
    background: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(24px) saturate(180%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.4);
  }

  .header-scrolled-refined {
    background: rgba(255, 255, 255, 0.90);
    backdrop-filter: blur(30px) saturate(200%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.6);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
  }

  .logo-link-refined {
    transition: all 0.3s ease;
  }
  .logo-link-refined:hover {
    opacity: 0.8;
    transform: scale(1.05);
  }

  /* ===== CARD TITLE ===== */
  .card-title-refined {
    font-size: 1rem;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.025em;
  }

  /* ===== MAIN ICON ===== */
  .main-icon-refined {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.6));
    backdrop-filter: blur(16px);
    border: 2px solid rgba(255, 255, 255, 0.6);
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08), inset 0 2px 0 rgba(255, 255, 255, 0.9);
  }

  /* ===== STATUS ICON ===== */
  .status-icon-refined {
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }

  /* ===== BADGES ===== */
  .badge-icp-refined {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(16, 185, 129, 0.08));
    border: 1px solid rgba(16, 185, 129, 0.25);
    border-radius: 8px;
    color: #059669;
    font-size: 0.7rem;
    font-weight: 600;
  }

  .badge-anvisa-refined {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(59, 130, 246, 0.08));
    border: 1px solid rgba(59, 130, 246, 0.25);
    border-radius: 8px;
    color: #2563eb;
    font-size: 0.7rem;
    font-weight: 600;
  }

  .partner-badge-active {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.1));
    border: 1px solid rgba(16, 185, 129, 0.3);
    border-radius: 8px;
    color: #059669;
    font-size: 0.65rem;
    font-weight: 700;
  }

  .status-badge-refined {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 700;
  }

  .status-badge-success {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.1));
    color: #065f46;
    border: 1px solid rgba(16, 185, 129, 0.3);
  }

  .status-badge-info {
    background: linear-gradient(135deg, rgba(100, 116, 139, 0.12), rgba(100, 116, 139, 0.08));
    color: #475569;
    border: 1px solid rgba(100, 116, 139, 0.25);
  }

  /* ===== SUBSCRIPTION INFO ===== */
  .subscription-info-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px;
    background: rgba(248, 250, 252, 0.6);
    border-radius: 12px;
  }

  /* ===== PLAN CARDS ===== */
  .plan-card-refined {
    padding: 20px;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(16px);
    border: 2px solid rgba(226, 232, 240, 0.6);
    border-radius: 18px;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
  }

  .plan-card-refined:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.08);
  }

  .plan-card-active {
    border-color: rgba(16, 185, 129, 0.4);
    box-shadow: 0 8px 24px rgba(16, 185, 129, 0.1);
  }

  .plan-card-partner {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.85), rgba(236, 253, 245, 0.6));
  }

  .plan-card-partner-active {
    border-color: rgba(16, 185, 129, 0.5);
    box-shadow: 0 12px 32px rgba(16, 185, 129, 0.15);
  }

  .plan-card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .plan-icon-refined {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    border: 1px solid;
  }

  .plan-icon-basic {
    background: rgba(248, 250, 252, 0.8);
    border-color: rgba(226, 232, 240, 0.8);
  }

  .plan-icon-partner {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.1));
    border-color: rgba(16, 185, 129, 0.3);
  }

  .plan-badge-current {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    background: rgba(100, 116, 139, 0.1);
    border: 1px solid rgba(100, 116, 139, 0.2);
    border-radius: 8px;
    color: #64748b;
    font-size: 0.65rem;
    font-weight: 600;
  }

  .plan-badge-active {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.1));
    border: 1px solid rgba(16, 185, 129, 0.3);
    border-radius: 8px;
    color: #059669;
    font-size: 0.65rem;
    font-weight: 600;
  }

  .plan-badge-recommended {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(245, 158, 11, 0.1));
    border: 1px solid rgba(245, 158, 11, 0.3);
    border-radius: 8px;
    color: #d97706;
    font-size: 0.65rem;
    font-weight: 600;
  }

  .plan-price-refined {
    margin-bottom: 16px;
  }

  .price-value {
    font-size: 1.75rem;
    font-weight: 800;
    color: #1e293b;
    letter-spacing: -0.03em;
  }

  .price-period {
    font-size: 0.85rem;
    color: #64748b;
    font-weight: 500;
  }

  .plan-features-refined {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .plan-feature-item {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .plan-feature-highlight {
    padding: 6px 10px;
    background: rgba(16, 185, 129, 0.06);
    border-radius: 8px;
    margin: -6px -10px;
  }

  .plan-feature-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    flex-shrink: 0;
  }

  .plan-feature-icon-basic {
    background: rgba(148, 163, 184, 0.1);
    color: #94a3b8;
  }

  .plan-feature-icon-partner {
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
  }

  /* ===== BUTTONS ===== */
  .button-primary-refined {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 20px;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border: none;
    border-radius: 14px;
    color: white;
    font-weight: 700;
    font-size: 0.8rem;
    transition: all 0.3s ease;
    box-shadow: 0 10px 32px rgba(16, 185, 129, 0.25), inset 0 2px 0 rgba(255, 255, 255, 0.25);
    cursor: pointer;
  }

  .button-primary-refined:hover:not(:disabled) {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 16px 40px rgba(16, 185, 129, 0.3);
  }

  .button-primary-refined:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .button-secondary-refined {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px 18px;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(16px);
    border: 2px solid rgba(255, 255, 255, 0.6);
    border-radius: 12px;
    color: #475569;
    font-weight: 600;
    font-size: 0.8rem;
    transition: all 0.3s ease;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8);
    cursor: pointer;
  }

  .button-secondary-refined:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.95);
    color: #1e293b;
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
  }

  .button-danger-refined {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px 18px;
    background: rgba(254, 242, 242, 0.9);
    backdrop-filter: blur(16px);
    border: 2px solid rgba(239, 68, 68, 0.3);
    border-radius: 12px;
    color: #dc2626;
    font-weight: 600;
    font-size: 0.8rem;
    transition: all 0.3s ease;
    cursor: pointer;
  }

  .button-danger-refined:hover:not(:disabled) {
    background: rgba(254, 226, 226, 0.95);
    border-color: rgba(239, 68, 68, 0.5);
    transform: translateY(-1px);
  }

  .button-danger-refined:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* ===== MARKETPLACE STEPS ===== */
  .marketplace-step-refined {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px;
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 14px;
    transition: all 0.3s ease;
  }

  .marketplace-step-refined:hover {
    background: rgba(255, 255, 255, 0.85);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  }

  .step-number-refined {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border-radius: 50%;
    color: white;
    font-size: 0.75rem;
    font-weight: 700;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  }

  .step-content-refined {
    flex: 1;
    min-width: 0;
  }

  /* ===== ROI CARD ===== */
  .roi-card-refined {
    position: relative;
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(20, 184, 166, 0.05) 100%);
    border: 2px solid rgba(16, 185, 129, 0.2);
    border-radius: 18px;
    overflow: hidden;
  }

  .roi-icon-refined {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7));
    border: 2px solid rgba(16, 185, 129, 0.3);
    border-radius: 14px;
    flex-shrink: 0;
    box-shadow: 0 6px 16px rgba(16, 185, 129, 0.15);
  }

  .roi-content-refined {
    flex: 1;
    position: relative;
    z-index: 1;
  }

  .roi-highlight-refined {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .roi-badge-refined {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border-radius: 8px;
    color: white;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  }

  .roi-decoration {
    position: absolute;
    top: -50%;
    right: -10%;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }

  /* ===== LOADING ===== */
  .loading-container-refined {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    gap: 20px;
  }

  .loading-animation-refined {
    position: relative;
  }

  .loading-glow-refined {
    position: absolute;
    inset: -18px;
    background: rgba(16, 185, 129, 0.15);
    border-radius: 50%;
    filter: blur(22px);
  }

  .loading-icon-refined {
    position: relative;
    width: 64px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border-radius: 50%;
    box-shadow: 0 12px 32px rgba(16, 185, 129, 0.3);
  }

  /* ===== NOTIFICATIONS ===== */
  .notification-refined {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(24px) saturate(200%);
    border: 1px solid rgba(255, 255, 255, 0.6);
    border-radius: 14px;
    padding: 14px 18px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
    animation: slideInUp 0.4s ease;
  }

  .notification-success-refined {
    border-color: rgba(16, 185, 129, 0.3);
    box-shadow: 0 20px 60px rgba(16, 185, 129, 0.15);
  }

  .notification-error-refined {
    border-color: rgba(239, 68, 68, 0.3);
    box-shadow: 0 20px 60px rgba(239, 68, 68, 0.15);
  }

  .notification-warning-refined {
    border-color: rgba(245, 158, 11, 0.3);
    box-shadow: 0 20px 60px rgba(245, 158, 11, 0.15);
  }

  .notification-info-refined {
    border-color: rgba(59, 130, 246, 0.3);
    box-shadow: 0 20px 60px rgba(59, 130, 246, 0.15);
  }

  .notification-icon-refined {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 9px;
    border: 1px solid rgba(255, 255, 255, 0.6);
  }

  .notification-close-refined {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 7px;
    transition: all 0.3s ease;
    cursor: pointer;
  }

  .notification-close-refined:hover {
    background: rgba(255, 255, 255, 0.9);
  }

  @keyframes slideInUp {
    0% {
      opacity: 0;
      transform: translateY(20px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* ===== FOOTER ===== */
  .footer-refined {
    background: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(20px);
    border-top: 1px solid rgba(255, 255, 255, 0.5);
    padding: 16px 0;
    margin-top: 56px;
  }

  /* ===== RESPONSIVE ===== */
  @media (max-width: 768px) {
    .plan-card-refined {
      padding: 16px;
    }
    
    .roi-card-refined {
      flex-direction: column;
      text-align: center;
      padding: 16px;
    }
    
    .roi-highlight-refined {
      justify-content: center;
    }
    
    .marketplace-step-refined {
      padding: 12px;
    }

    .subscription-info-item {
      padding: 10px;
    }
  }

  /* ===== ACCESSIBILITY ===== */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      transition-duration: 0.01ms !important;
    }
  }

  @media (prefers-contrast: high) {
    .glass-card-refined, 
    .glass-card-elevated-refined,
    .plan-card-refined {
      background: rgba(255, 255, 255, 0.95);
      border: 2px solid rgba(0, 0, 0, 0.2);
    }
  }

  @media (pointer: coarse) {
    button, a, [role="button"] { 
      min-height: 44px; 
      min-width: 44px; 
    }
  }
`;